import { alpha } from '@mui/material'
import theme from 'assets/theme'
import { Chart as ChartJS, type ChartOptions, type TooltipItem, type TooltipLabelStyle } from 'chart.js'
import { getDateFormat, getDateLocale, localDate } from 'global/helpers/dates'
import { formatDecimals } from 'global/helpers/numberFormatters'
import { getFormattedUnit } from 'global/helpers/units'
import { merge } from 'lodash'

ChartJS.defaults.font.family = 'SuisseIntl'
ChartJS.defaults.font.size = 12

const getBaseChartDefaults = (): ChartOptions => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      align: 'center',
      labels: {
        boxWidth: 12,
        boxHeight: 12,
        padding: 8,
        font: {
          size: 12,
          weight: 400,
        },
        useBorderRadius: true,
        borderRadius: 2,
      },
    },
    tooltip: {
      displayColors: true,
      boxWidth: 12,
      boxHeight: 12,
      boxPadding: 4,
      bodyFont: {
        size: 16,
      },
      padding: 8,
      cornerRadius: 8,
      multiKeyBackground: 'transparent',
      backgroundColor: alpha(theme.palette.primary.dark, 0.85),
      titleColor: theme.palette.primary.light,
      titleMarginBottom: 4,
      callbacks: {
        labelColor: (context): TooltipLabelStyle => ({
          borderColor: context.dataset.borderColor as string,
          backgroundColor: context.dataset.backgroundColor as string,
          borderWidth: 0,
          borderRadius: 2,
        }),
      },
    },
  },
  elements: {
    point: {
      backgroundColor: theme.palette.common.white,
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
      radius: 2,
    },
    line: {
      borderWidth: 2,
      borderColor: theme.palette.primary.main,
    },
  },
  scales: {
    x: {
      grid: {
        color: theme.palette.primary.lighter,
      },
      ticks: {
        color: theme.palette.primary.main,
        padding: 4,
        font: {
          size: 10,
          weight: 600,
        },
      },
      border: {
        color: theme.palette.primary.light,
      },
    },
    y: {
      grid: {
        color: theme.palette.primary.lighter,
      },
      ticks: {
        color: theme.palette.primary.main,
        padding: 4,
        font: {
          size: 10,
          weight: 600,
        },
      },
      border: {
        color: theme.palette.primary.light,
      },
    },
  },
})

const getLineChartDefaults = (): ChartOptions<'line'> => ({
  plugins: {
    legend: {
      display: true,
    },
  },
  interaction: {
    mode: 'index',
    intersect: false,
  },
})

const getTimeChartDefaults = (): ChartOptions => ({
  plugins: {
    tooltip: {
      callbacks: {
        title: (context: TooltipItem<'line'>[]) => {
          // NOTE: is context an array with multiple elements when there are multiple charts overlapping?
          return localDate(new Date(context[0]!.parsed.x), 'full') as string
        },
      },
    },
  },
  elements: {
    point: {
      pointStyle: false,
    },
    line: {
      tension: 0.2,
      cubicInterpolationMode: 'monotone' as const,
    },
  },
  scales: {
    x: {
      adapters: {
        date: {
          locale: getDateLocale(),
        },
      },
      type: 'time',
      time: {
        minUnit: 'hour',
        displayFormats: {
          hour: getDateFormat('short_no_year_with_time'),
          day: getDateFormat('medium_no_year'),
        },
      },
      ticks: {
        minRotation: 0,
        maxRotation: 45,
        major: {
          enabled: true,
        },
        callback: (value, index, values) =>
          localDate(new Date(value), values[index]!.major ? 'medium_no_year' : 'time') as string,
      },
    },
    y: {
      grid: {
        drawTicks: false,
      },
      ticks: {
        padding: 8,
      },
      border: {
        display: false,
      },
    },
  },
})

const getTemperatureChartDefaults = (unit: string): ChartOptions => ({
  plugins: {
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'line'>) => `${formatDecimals(context.parsed.y)} ${getFormattedUnit(unit)}`,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      ticks: {
        // NOTE: value can be a string or a number
        callback: (value) => `${formatDecimals(value)} ${getFormattedUnit(unit)}`,
      },
    },
  },
})

export const getDarkModeColors = (): ChartOptions => ({
  plugins: {
    legend: {
      labels: {
        color: theme.palette.common.white,
      },
    },
  },
  elements: {
    point: {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.common.white,
    },
    line: {
      borderColor: theme.palette.common.white,
      borderWidth: 1,
    },
  },
  scales: {
    x: {
      grid: {
        color: alpha(theme.palette.primary.light, 0.25),
      },
      ticks: {
        color: theme.palette.primary.light,
      },
      border: {
        color: alpha(theme.palette.primary.light, 0.25),
      },
    },
    y: {
      grid: {
        color: alpha(theme.palette.primary.light, 0.25),
      },
      ticks: {
        color: theme.palette.primary.light,
      },
      border: {
        color: alpha(theme.palette.primary.light, 0.25),
      },
    },
  },
})

export const getDatasetColors = () => [
  '#b197fc', // violet
  '#ffa8a8', // red
  '#ffe066', // yellow
  '#63e6be', // teal
  '#74c0fc', // blue
]

export interface Annotation {
  drawTime: string
  type: string
  mode?: string
  yMin?: number
  yMax?: number
  xMin?: number
  xMax?: number
  scaleID?: string
  xScaleID?: string
  value?: string
  backgroundColor?: string
  borderWidth?: number
  borderColor?: string
  label?: AnnotationLabel
  enter?: ({ element }: { element: any; }, event: any) => boolean
  leave?: ({ element }: { element: any; }, event: any) => boolean
  borderDash?: number[]
}

export interface AnnotationLabel {
  content: string,
  display: boolean,
  position: string,
}

export const getLineChartOptions = (extendedChartOptions: ChartOptions<'line'> = {}): ChartOptions<'line'> =>
  merge(getBaseChartDefaults(), getLineChartDefaults(), extendedChartOptions)

export const getTemperatureChartOptions = (unit: string, extendedChartOptions: ChartOptions<'line'> = {}): ChartOptions<'line'> =>
  merge(
    getBaseChartDefaults(),
    getLineChartDefaults(),
    getTimeChartDefaults(),
    getTemperatureChartDefaults(unit),
    extendedChartOptions,
  )
